import type { AdProvider, AdvertisingOverviewTrend, TrendGranularity } from '@/api/advertisingOverview'
import type { AttributionModel } from '@/api/types'
import { tmtAttributedLabel } from '@/components/TmtAttributedBadge'
import { Card, DualSeriesChart, EmptyState, formatInteger, majorUnits, SegmentedControl } from '@/design'
import { formatMoney, windowLabel } from '@/features/analytics/advertising/metrics'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { formatDateKey, type GlobalFilters } from '@/lib/filters'

import { NOT_AVAILABLE_TEXT } from './definitions'
import { advertisingSectionError, isAdvertisingFlagOff } from './errors'
import { useAdvertisingOverviewTrend, useGranularity } from './overviewQuery'
import { FlagOffNote } from './SectionStates'

const GRANULARITY_OPTIONS: readonly { value: TrendGranularity; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
]

/** The chart and its totals, or why there are none. */
function TrendBody({ data, granularity }: { data: AdvertisingOverviewTrend; granularity: TrendGranularity }) {
  if (data.notAvailable) {
    const text = NOT_AVAILABLE_TEXT[data.notAvailable]
    return <EmptyState variant="not-live" compact title={text.label} description={text.why} />
  }
  const currency = data.meta.currency
  const points = data.points.map((point) => ({
    label: formatDateKey(point.date),
    detail:
      point.date === point.endDate ? formatDateKey(point.date, true) : `${formatDateKey(point.date)} – ${formatDateKey(point.endDate, true)}`,
    primary: majorUnits(point.spendMinor, currency),
    secondary: majorUnits(point.attributedRevenueMinor, currency),
  }))
  return (
    <>
      <DualSeriesChart
        title={`Ad spend and attributed revenue by ${granularity}`}
        points={points}
        primaryLabel="Ad spend (Meta)"
        secondaryLabel={`Attributed revenue (${tmtAttributedLabel(data.meta.model)})`}
        format="currency"
        formatOptions={{ currency }}
      />
      {data.totals ? (
        <p className="aov-note" data-testid="trend-totals">
          {windowLabel(data.meta.range.from, data.meta.range.to)}: {formatMoney(data.totals.spendMinor, currency)} spend ·{' '}
          {formatMoney(data.totals.attributedRevenueMinor, currency)} attributed revenue ·{' '}
          {formatInteger(data.totals.purchases)} attributed purchases
        </p>
      ) : null}
    </>
  )
}

/**
 * Ad spend (Meta's, columns) against the revenue TmT attributed to the rows with spend
 * (a line), by day or week, on one money axis. Its own query: a failure here leaves
 * the rest of the page standing.
 */
export function SpendRevenueTrendCard({
  filters,
  provider,
  model,
}: {
  filters: GlobalFilters
  provider: AdProvider
  model: AttributionModel
}) {
  const [granularity, setGranularity] = useGranularity()
  const trend = useAdvertisingOverviewTrend(filters, provider, model, granularity)
  return (
    <Card
      title="Ad spend vs attributed revenue"
      subtitle={`Spend reported by Meta · revenue ${tmtAttributedLabel(model)}`}
      actions={<SegmentedControl label="Chart granularity" options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} />}
      busy={trend.isFetching}
    >
      {isAdvertisingFlagOff(trend.error) ? (
        <FlagOffNote />
      ) : (
        <CardQueryBody
          query={trend}
          loadingRows={5}
          errorDescription={advertisingSectionError(trend.error, 'The spend and revenue trend could not be loaded.')}
        >
          {(data) => <TrendBody data={data} granularity={granularity} />}
        </CardQueryBody>
      )}
    </Card>
  )
}
